import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CaretDown, MagnifyingGlass, X, Star } from '@phosphor-icons/react';
import { BounceLoader, MoonLoader } from 'react-spinners';
import colors from './assets/colors';
import { JiraProject, Product, Project, Release, Section } from '@Models/types';

// union for type guards only
type SelectableItem = Product | Project | JiraProject | Release | Section; //build error fix: added release and section

interface Option<T> {
  label: string;
  value: T;
}

interface SelectProps<T> {
  onOpen?: () => void;
  options: Option<T>[];
  onChange: (value: Option<T> | null) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  type: 'product' | 'project' | 'jira-project' | 'release' | 'TestRail Section'; //build error fix: added release and TestRail Section
  favorites?: {
    enabled: boolean;
    type:
      | 'product'
      | 'project'
      | 'jira-project'
      | 'release'
      | 'TestRail Section'; //build error fix: added release and TestRail Section

    fetch: () => Promise<T[]>;

    save: (items: T[]) => Promise<void | T[]>;
    favoriteKey: 'reference_prefix' | 'id' | 'key';
  };
  showAlert?: boolean;
  alertMessage?: string;
}

const Select = <T,>({
  onOpen,
  options,
  onChange,
  placeholder,
  loading = false,
  disabled = false,
  type,
  favorites,
  showAlert = false,
  alertMessage,
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option<T> | null>(null);
  const [localFavorites, setLocalFavorites] = useState<Option<T>[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const dropDownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (favorites?.enabled) {
      const loadFavorites = async () => {
        try {
          const favs = await favorites.fetch();
          setLocalFavorites(
            favs.map((fav) => {
              const item = fav as unknown as SelectableItem;
              return {
                label: item.name,
                value: fav,
              };
            })
          );
        } catch (err) {
          console.error('Error loading favorites:', err);
        }
      };
      loadFavorites();
    }
  }, [favorites]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const getItemId = (value: T): string => {
    const item = value as unknown as SelectableItem;
    if (value !== null && typeof value === 'object') {
      if ('reference_prefix' in item) return item.reference_prefix;
      if ('key' in item) return item.key;
      if ('id' in item) return String((item as Project).id);
    }

    return String(value);
  };

  const handleToggleFavorite = async (option: Option<T>) => {
    if (!favorites || processingId) return;

    const optionValue = option.value;
    const currentId = getItemId(optionValue);

    try {
      setProcessingId(currentId);

      const isFavorite = localFavorites.some((fav) => {
        return getItemId(fav.value) === currentId;
      });

      const updatedFavorites = isFavorite
        ? localFavorites.filter((fav) => {
            return getItemId(fav.value) !== currentId;
          })
        : [...localFavorites, option];

      await favorites.save(updatedFavorites.map((fav) => fav.value));
      setLocalFavorites(updatedFavorites);
    } catch (error) {
      console.error('Failed to update favorites:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const favoriteOptions = useMemo(
    () =>
      localFavorites.filter((fav) =>
        fav.label.toLowerCase().includes(search.toLowerCase())
      ),
    [localFavorites, search]
  );

  const nonFavoriteOptions = useMemo(
    () =>
      filteredOptions.filter(
        (option) =>
          !localFavorites.some((fav) => {
            return getItemId(fav.value) === getItemId(option.value);
          })
      ),
    [filteredOptions, localFavorites]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => setSearch('');

  const handleOptionClick = (option: Option<T>) => {
    setSelectedOption(option);
    setIsOpen(false);
    onChange(option);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (onOpen) onOpen();
    }
  };

  return (
    <div
      ref={dropDownRef}
      role="combobox"
      aria-expanded={isOpen ? 'true' : 'false'}
      className="relative w-full"
    >
      <div
        data-testid="test-select-toggle"
        className={`flex ${loading ? 'cursor-auto' : 'cursor-pointer'} items-center justify-between rounded-md border border-gray-300 px-4 py-2`}
        onClick={toggleDropdown}
      >
        {showAlert ? (
          <span className="animate-pulse rounded text-sm font-semibold text-red-800">
            {alertMessage || 'Select From Dropdown!'}
          </span>
        ) : (
          <span
            className={`font-inter text-sm text-neutral-600 ${loading && 'animate-pulse'}`}
          >
            {selectedOption
              ? selectedOption.label
              : placeholder || 'Select an option'}
          </span>
        )}

        {loading ? (
          <BounceLoader
            data-testid="Loader"
            size={20}
            color={colors.brandWarning[500]}
          />
        ) : (
          <CaretDown
            weight="bold"
            className={`transform hover:text-primary-500 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>
      {isOpen && (
        <div
          role="listbox"
          className="absolute z-10 mt-1 w-full rounded border border-gray-300 bg-white p-2 shadow-lg"
        >
          <div className="flex flex-row items-center gap-x-2 rounded-md border px-4 py-2">
            <MagnifyingGlass size={20} weight="bold" />
            <input
              data-testid="test-select-search-input"
              type="text"
              className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
              placeholder={`Search ${type}`}
              value={search}
              onChange={handleSearch}
            />
            <X
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          </div>

          <ul className="custom-scroll max-h-60 overflow-auto px-2 py-1 font-inter text-xs sm:text-sm">
            {loading ? (
              <span className="flex h-full w-full flex-col items-center justify-center gap-y-4 p-5">
                <MoonLoader size={50} color="#071D49" />
                <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600">
                  {type}s Loading
                </span>
              </span>
            ) : (
              <>
                {favorites?.enabled && favoriteOptions.length > 0 && (
                  <div className="mb-2 flex flex-col gap-y-2 border-b border-gray-300 pb-2">
                    <span className="text-xs font-bold text-gray-500 sm:text-sm">
                      Favorites:
                    </span>
                    {favoriteOptions.map((option, index) => {
                      const itemId = getItemId(option.value);

                      return (
                        <li
                          key={`fav-${index}`}
                          className="flex cursor-pointer items-center justify-between rounded-md px-4 py-2 hover:bg-primary-100"
                        >
                          <div
                            onClick={() => handleOptionClick(option)}
                            className="flex-1"
                          >
                            {option.label}
                          </div>
                          <div
                            onClick={() =>
                              !processingId && handleToggleFavorite(option)
                            }
                            className={`${processingId ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {processingId === itemId ? (
                              <MoonLoader
                                size={16}
                                color={colors.brandWarning[500]}
                              />
                            ) : (
                              <Star
                                weight="fill"
                                className="text-yellow-500 hover:text-yellow-600"
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </div>
                )}

                {nonFavoriteOptions.map((option, index) => {
                  const itemId = getItemId(option.value);
                  const isProcessing = processingId === itemId;

                  return (
                    <li
                      key={`nonfav-${index}`}
                      className={`flex items-center justify-between rounded-md px-4 py-2 ${
                        !isProcessing ? 'hover:bg-primary-100' : ''
                      } ${
                        option === selectedOption
                          ? 'bg-primary-700 font-bold text-white'
                          : ''
                      }`}
                    >
                      <div
                        onClick={() =>
                          !isProcessing && handleOptionClick(option)
                        }
                        className={`flex-1 ${isProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {option.label}
                      </div>
                      {favorites?.enabled && (
                        <div
                          onClick={() =>
                            !isProcessing && handleToggleFavorite(option)
                          }
                          className={`${isProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {isProcessing ? (
                            <MoonLoader
                              size={16}
                              color={colors.brandWarning[500]}
                            />
                          ) : (
                            <Star
                              weight={
                                localFavorites.some(
                                  (fav) => getItemId(fav.value) === itemId
                                )
                                  ? 'fill'
                                  : 'bold'
                              }
                              className={`${
                                localFavorites.some(
                                  (fav) => getItemId(fav.value) === itemId
                                )
                                  ? 'text-yellow-500'
                                  : 'text-gray-400'
                              } hover:text-yellow-600`}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}

                {!loading && filteredOptions.length === 0 && (
                  <li className="px-4 py-2 text-gray-500">No {type}s found</li>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Select;
